import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { StatsCard } from '../components/StatsCard';
import { statsAPI, customerAPI, loadAPI } from '../api';
import { toast } from 'sonner';
import { DollarSign, Package, TrendingUp } from 'lucide-react';

const Reports = () => {
  const [stats, setStats] = useState(null);
  const [customerStats, setCustomerStats] = useState([]);
  const [carrierStats, setCarrierStats] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, customersRes, loadsRes, carrierPerfRes] = await Promise.all([
        statsAPI.getDashboard(),
        customerAPI.getAll(),
        loadAPI.getAll(),
        statsAPI.getCarrierPerformance()
      ]);
      
      setStats(statsRes.data);
      
      // Helper to get revenue (supports both old and new field names)
      const getLoadRevenue = (load) => {
        const lineHaul = load.customer_line_haul || 0;
        const accessorials = load.customer_accessorials || 0;
        const fsc = load.fsc_enabled ? (load.fsc_amount || 0) : 0;
        if (lineHaul > 0) return lineHaul + accessorials + fsc;
        return load.customer_rate || 0;
      };
      
      const getLoadMargin = (load) => {
        const fsc = load.fsc_enabled ? (load.fsc_amount || 0) : 0;
        const customerTotal = (load.customer_line_haul || 0) + (load.customer_accessorials || 0) + fsc;
        const carrierTotal = (load.carrier_line_haul || 0) + (load.carrier_accessorials || 0);
        if (customerTotal > 0) return customerTotal - carrierTotal;
        return load.margin || 0;
      };
      
      const customerLoadCounts = customersRes.data.map(customer => {
        const loads = loadsRes.data.filter(l => l.customer_id === customer.id);
        const totalRevenue = loads.reduce((sum, l) => sum + getLoadRevenue(l), 0);
        const totalMargin = loads.reduce((sum, l) => sum + getLoadMargin(l), 0);
        return { name: customer.name, loads: loads.length, revenue: totalRevenue, margin: totalMargin };
      }).sort((a, b) => b.revenue - a.revenue).slice(0, 10);
      
      setCustomerStats(customerLoadCounts);
      setCarrierStats(carrierPerfRes.data);
    } catch (error) {
      toast.error('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-[var(--tms-text-secondary)]">Loading reports...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" data-testid="reports-page">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-[var(--tms-text)]">Reports & Analytics</h1>
          <p className="text-sm md:text-base text-[var(--tms-text-secondary)] mt-1">Performance metrics and insights</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <StatsCard title="Total Revenue" value={`$${stats?.total_revenue?.toLocaleString() || 0}`} icon={DollarSign} testId="stat-revenue" />
          <StatsCard title="Total Margin" value={`$${stats?.total_margin?.toLocaleString() || 0}`} icon={TrendingUp} testId="stat-margin" />
          <StatsCard title="Avg Margin %" value={`${stats?.avg_margin_pct?.toFixed(1) || 0}%`} icon={TrendingUp} testId="stat-avg-margin" />
          <StatsCard title="Total Loads" value={stats?.total_loads || 0} icon={Package} testId="stat-loads" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="stats-card">
            <h3 className="text-lg font-semibold text-[var(--tms-text)] mb-4">Top Customers by Revenue</h3>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Loads</th>
                    <th>Revenue</th>
                    <th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {customerStats.length === 0 ? (
                    <tr><td colSpan={4} className="text-center py-8 text-[var(--tms-text-muted)]">No data yet</td></tr>
                  ) : (
                    customerStats.map((customer, idx) => (
                      <tr key={customer.id || customer.customer_id || customer.name} data-testid={`customer-stat-${idx}`}>
                        <td className="font-semibold">{customer.name}</td>
                        <td className="font-mono">{customer.loads}</td>
                        <td className="font-mono">${customer.revenue.toLocaleString()}</td>
                        <td className="font-mono text-[var(--tms-success)]">${customer.margin.toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="stats-card">
            <h3 className="text-lg font-semibold text-[var(--tms-text)] mb-4">Carrier Performance</h3>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr><th>Carrier</th><th>Loads</th><th>On-Time Pickup</th><th>On-Time Delivery</th></tr>
                </thead>
                <tbody>
                  {carrierStats.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-8 text-[var(--tms-text-muted)]">No carrier data yet</td></tr>
                  ) : (
                    carrierStats.map((carrier, idx) => (
                      <tr key={carrier.id || carrier.carrier_id || carrier.name} data-testid={`carrier-stat-${idx}`}>
                        <td className="font-semibold">{carrier.name}</td>
                        <td className="font-mono">{carrier.total_loads}</td>
                        <td className="font-mono">
                          <span className={carrier.on_time_pickup_pct >= 90 ? 'text-[var(--tms-success)]' : carrier.on_time_pickup_pct >= 70 ? 'text-[var(--tms-warning)]' : 'text-[var(--tms-error)]'}>
                            {carrier.on_time_pickup_pct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="font-mono">
                          <span className={carrier.on_time_delivery_pct >= 90 ? 'text-[var(--tms-success)]' : carrier.on_time_delivery_pct >= 70 ? 'text-[var(--tms-warning)]' : 'text-[var(--tms-error)]'}>
                            {carrier.on_time_delivery_pct.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Reports;